import { useState } from 'react';
import type { ReactNode } from 'react';
import { accentSoftWash } from '@yourstore/domain';
import { useAction, useApi } from '../../../context';
import { Failure } from '../../../bits';
import { useT } from '../../../../messages';
import type { T } from '../../../../messages';
import { APP_ACCENT, APP_GROUND, wordCount } from './AppPreview';

/**
 * **The named set as friendly controls** (console redesign B8).
 *
 * Seven fields and no more (`docs/lovespa-personality.md`: "the names above
 * are the whole set until a real tenant needs a sixth"), each drawn as what it
 * is to an operator rather than as its field name: two colour wells, three
 * either/or choices, and two photograph slots that take a file from this
 * computer.
 *
 * The draft is the flat `field → value` map the old editor used, and `''` —
 * a missing key — means "not set", which is the app's own default. Picking the
 * first option of a choice therefore *removes* the field rather than writing
 * the default out, so a saved look names only what the business chose.
 */

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const HEX6 = /^#[0-9a-f]{6}$/i;

/** What the upload route accepts, restated for the sentence before the trip. */
const UPLOADABLE = ['image/jpeg', 'image/png', 'image/webp'];
/** 10 MB — `CATALOG_UPLOAD_MAX_BYTES` on `admin-api`, which stays the fence. */
const MAX_BYTES = 10_485_760;

export type LookDraft = Record<string, string>;

/** A colour value the server will take, or `''`: the Save button's one pre-check. */
export function colourOk(value: string | undefined): boolean {
  return value === undefined || value === '' || HEX.test(value);
}

function refusalOf(t: T, file: File): string | null {
  const name = file.name.toLowerCase();
  if (
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  ) {
    return t('look.upload.heic', { file: file.name });
  }
  if (!UPLOADABLE.includes(file.type)) return t('look.upload.notPicture', { file: file.name });
  if (file.size > MAX_BYTES) return t('look.upload.tooBig', { file: file.name });
  return null;
}

export function LookEditor({
  tenantId,
  name,
  draft,
  uploadsConfigured,
  onChange,
}: {
  tenantId: string;
  name: string;
  draft: LookDraft;
  uploadsConfigured: boolean;
  onChange: (next: LookDraft) => void;
}): ReactNode {
  const t = useT();

  const set = (field: string, value: string): void => {
    const next = { ...draft };
    if (value === '') delete next[field];
    else next[field] = value;
    onChange(next);
  };

  const words = wordCount(name);

  return (
    <div className="look-editor">
      <Colour
        label={t('look.accent.label')}
        hint={t('look.accent.hint')}
        value={draft['accent'] ?? ''}
        fallback={APP_ACCENT}
        wash
        onChange={(v) => set('accent', v)}
      />
      <Colour
        label={t('look.ground.label')}
        hint={t('look.ground.hint')}
        value={draft['ground'] ?? ''}
        fallback={APP_GROUND}
        onChange={(v) => set('ground', v)}
      />

      <Choice
        legend={t('look.font.label')}
        hint={t('look.font.hint')}
        field="font_display"
        value={draft['font_display'] ?? ''}
        options={[
          { value: 'system', label: t('look.font.system') },
          { value: 'serif', label: t('look.font.serif') },
        ]}
        onChange={(v) => set('font_display', v)}
      />

      <Choice
        legend={t('look.mark.label')}
        hint={t('look.mark.hint')}
        field="brandmark"
        value={draft['brandmark'] ?? ''}
        options={[
          { value: 'plain', label: t('look.mark.plain') },
          { value: 'hearts', label: t('look.mark.hearts') },
        ]}
        onChange={(v) => set('brandmark', v)}
      >
        {/* The three-word rule, in words, where it bites: the app falls back
            silently, so this is where an operator has to find out. */}
        {draft['brandmark'] === 'hearts' && words < 3 ? (
          <p className="look-warning" role="note">
            {t('look.mark.short', { name, words })}
          </p>
        ) : null}
      </Choice>

      <Choice
        legend={t('look.button.label')}
        hint={t('look.button.hint')}
        field="action_icon"
        value={draft['action_icon'] ?? ''}
        options={[
          { value: 'plus', label: t('look.button.plus') },
          { value: 'heart', label: t('look.button.heart') },
        ]}
        onChange={(v) => set('action_icon', v)}
      />

      <fieldset className="look-group">
        <legend>{t('look.photos.legend')}</legend>
        <p className="field__hint">{t('look.photos.hint')}</p>
        <Photo
          tenantId={tenantId}
          slot={t('look.photo.portrait')}
          value={draft['background_portrait_url'] ?? ''}
          uploadsConfigured={uploadsConfigured}
          onChange={(v) => set('background_portrait_url', v)}
        />
        <Photo
          tenantId={tenantId}
          slot={t('look.photo.landscape')}
          value={draft['background_landscape_url'] ?? ''}
          uploadsConfigured={uploadsConfigured}
          onChange={(v) => set('background_landscape_url', v)}
        />
        <p className="field__hint">
          {uploadsConfigured ? t('look.upload.hint') : t('look.upload.off')}
        </p>
      </fieldset>
    </div>
  );
}

/**
 * A colour: a well and its hex code, which agree about the whole value space —
 * the named set admits hex and nothing else, because the soft wash is worked
 * out from it. "Use the app's own" empties it.
 */
function Colour({
  label,
  hint,
  value,
  fallback,
  wash = false,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  fallback: string;
  wash?: boolean;
  onChange: (value: string) => void;
}): ReactNode {
  const t = useT();
  const shown = value === '' ? fallback : value;
  const soft = wash ? accentSoftWash(HEX.test(shown) ? shown : undefined) : null;
  return (
    <div className="look-group look-colour">
      <span className="field__label">{label}</span>
      <div className="look-colour__row">
        <input
          type="color"
          aria-label={t('look.colour.picker', { label })}
          value={HEX6.test(shown) ? shown : fallback}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          className="look-colour__hex"
          aria-label={t('look.colour.hex', { label })}
          value={value}
          placeholder={fallback}
          aria-invalid={!colourOk(value)}
          spellCheck={false}
          onChange={(e) => onChange(e.target.value.trim())}
        />
        {soft === null ? null : (
          <span className="look-colour__wash" title={soft}>
            <span className="look-colour__swatch" style={{ background: soft }} />
            {t('look.wash')}
          </span>
        )}
        {value === '' ? (
          <span className="faint">{t('look.colour.isStandard')}</span>
        ) : (
          <button
            type="button"
            className="button button--quiet button--sm"
            onClick={() => onChange('')}
          >
            {t('look.colour.standard')}
          </button>
        )}
      </div>
      {colourOk(value) ? null : <span className="look-warning">{t('look.colour.invalid')}</span>}
      <span className="field__hint">{hint}</span>
    </div>
  );
}

/** An either/or, as radios. The first option is the app's default and saves as "not set". */
function Choice({
  legend,
  hint,
  field,
  value,
  options,
  onChange,
  children,
}: {
  legend: string;
  hint: string;
  field: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
  children?: ReactNode;
}): ReactNode {
  return (
    <fieldset className="look-group look-choice">
      <legend>{legend}</legend>
      {options.map((option, index) => (
        <label key={option.value} className="look-choice__option">
          <input
            type="radio"
            name={`look-${field}`}
            checked={value === option.value || (value === '' && index === 0)}
            onChange={() => onChange(index === 0 ? '' : option.value)}
          />
          <span>{option.label}</span>
        </label>
      ))}
      {children}
      <span className="field__hint">{hint}</span>
    </fieldset>
  );
}

/**
 * One photograph slot: what is there, a file from this computer, or a pasted
 * address. An upload publishes the file and puts its address in the draft;
 * the look's Save is what hangs it on the business.
 */
function Photo({
  tenantId,
  slot,
  value,
  uploadsConfigured,
  onChange,
}: {
  tenantId: string;
  slot: string;
  value: string;
  uploadsConfigured: boolean;
  onChange: (value: string) => void;
}): ReactNode {
  const t = useT();
  const api = useApi();
  const upload = useAction();
  const [refused, setRefused] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [badAddress, setBadAddress] = useState(false);

  const send = async (file: File): Promise<void> => {
    const why = refusalOf(t, file);
    setRefused(why);
    if (why !== null) return;
    await upload.run(async () => {
      onChange(await api.uploadTenantAsset(tenantId, 'staff-background', file));
    });
  };

  return (
    <div className="look-photo">
      <div className="look-photo__thumb" aria-hidden="true">
        {value === '' ? null : <img src={value} alt="" loading="lazy" />}
      </div>
      <div className="look-photo__body">
        <strong>{slot}</strong>
        {value === '' ? (
          <span className="faint">{t('look.photo.none')}</span>
        ) : (
          <span className="look-photo__url" title={value}>
            {value.split('/').pop()}
          </span>
        )}
        <div className="row-actions">
          {uploadsConfigured ? (
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              aria-label={t('look.photo.upload', { slot })}
              disabled={upload.busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Cleared so choosing the same file again (after fixing it)
                // fires another change.
                e.target.value = '';
                if (file !== undefined) void send(file);
              }}
            />
          ) : null}
          {value === '' ? null : (
            <button
              type="button"
              className="button button--quiet button--sm"
              aria-label={t('look.photo.remove', { slot })}
              onClick={() => onChange('')}
            >
              {t('look.photo.removeShort')}
            </button>
          )}
        </div>
        <div className="look-photo__paste">
          <input
            aria-label={t('look.photo.paste', { slot })}
            placeholder="https://…"
            value={address}
            aria-invalid={badAddress}
            onChange={(e) => {
              setBadAddress(false);
              setAddress(e.target.value);
            }}
          />
          <button
            type="button"
            className="button button--sm"
            disabled={address.trim() === ''}
            onClick={() => {
              const next = address.trim();
              if (!next.startsWith('https://')) {
                setBadAddress(true);
                return;
              }
              onChange(next);
              setAddress('');
            }}
          >
            {t('look.photo.use')}
          </button>
        </div>
        {badAddress ? (
          <span className="look-warning">{t('look.photo.badAddress')}</span>
        ) : (
          <span className="field__hint">{t('look.photo.pasteHint')}</span>
        )}
        {upload.busy ? (
          <span className="faint" role="status">
            {t('look.photo.uploading')}
          </span>
        ) : null}
        {refused === null ? null : (
          <p className="look-warning" role="alert">
            {refused}
          </p>
        )}
        {upload.failure === null ? null : <Failure failure={upload.failure} />}
      </div>
    </div>
  );
}
